import { cx } from 'utils';

/**
 * EmptyState — friendly placeholder for empty lists, zero search results, and
 * first-run screens. Centers an icon tile, title, description, and optional
 * action(s).
 *
 * @param {node}   [icon]        Icon rendered inside the gradient tile.
 * @param {string} title
 * @param {string} [description]
 * @param {node}   [action]      Primary CTA (e.g. a Button).
 * @param {node}   [secondary]   Secondary action.
 * @param {'sm'|'md'|'lg'} [size='md']
 */
const SIZES = {
  sm: 'py-8',
  md: 'py-14',
  lg: 'py-20',
};

export default function EmptyState({
  icon,
  title,
  description,
  action,
  secondary,
  size = 'md',
  className = '',
  ...props
}) {
  return (
    <div
      className={cx(
        'flex flex-col items-center justify-center px-6 text-center',
        SIZES[size] || SIZES.md,
        className
      )}
      {...props}
    >
      <div className="relative mb-5">
        <div className="absolute inset-0 -z-10 rounded-3xl bg-brand-100/60 blur-2xl" />
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-gradient-soft text-brand-600 ring-1 ring-inset ring-brand-100">
          {icon || <DefaultIcon />}
        </span>
      </div>

      <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm text-slate-500">{description}</p>
      )}

      {(action || secondary) && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {action}
          {secondary}
        </div>
      )}
    </div>
  );
}

/** EmptyState.Error — convenience preset for failed loads. */
export function ErrorState({ title = 'Something went wrong', description, action, className = '' }) {
  return (
    <EmptyState
      className={className}
      title={title}
      description={description || 'We hit a snag loading this. Please try again.'}
      action={action}
      icon={
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 9v4m0 4h.01M10.3 4.3 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
    />
  );
}

function DefaultIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7a2 2 0 0 1 2-2h5l2 2h5a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
